import { gql } from "@apollo/client";

// Destination fragments
export const DESTINATION_FRAGMENT = gql`
  fragment DestinationFields on Destination {
    id
    tenantId
    name
    url
    category
    description
    tags
    isActive
    trackingEnabled
    conversationIds
    analytics {
      totalClicks
      uniqueClicks
      conversionRate
      lastClickAt
      clicksByDevice {
        PHONE
        DESKTOP
        TABLET
        OTHER
      }
      clicksByLocation {
        country
        region
        city
        clicks
      }
      clicksByHour {
        hour
        clicks
        date
      }
    }
    metadata {
      favicon
      title
      ogImage
      customProperties
    }
    createdAt
    updatedAt
    version
  }
`;

export const DESTINATION_ANALYTICS_FRAGMENT = gql`
  fragment DestinationAnalyticsFields on DestinationAnalytics {
    totalClicks
    uniqueClicks
    conversionRate
    lastClickAt
    clicksByDevice {
      PHONE
      DESKTOP
      TABLET
      OTHER
    }
    clicksByLocation {
      country
      region
      city
      clicks
    }
    clicksByHour {
      hour
      clicks
      date
    }
  }
`;

// Queries
export const LIST_DESTINATIONS = gql`
  query ListDestinations(
    $filter: DestinationFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listDestinations(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        ...DestinationFields
      }
      nextToken
      total
    }
  }
  ${DESTINATION_FRAGMENT}
`;

export const GET_DESTINATION = gql`
  query GetDestination($id: ID!) {
    getDestination(id: $id) {
      ...DestinationFields
    }
  }
  ${DESTINATION_FRAGMENT}
`;

export const GET_DESTINATION_ANALYTICS = gql`
  query GetDestinationAnalytics(
    $destinationId: ID!
    $dateFrom: String
    $dateTo: String
  ) {
    getDestinationAnalytics(
      destinationId: $destinationId
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      ...DestinationAnalyticsFields
    }
  }
  ${DESTINATION_ANALYTICS_FRAGMENT}
`;

export const GET_DESTINATIONS_BY_CONVERSATION = gql`
  query GetDestinationsByConversation(
    $conversationId: ID!
    $limit: Int
    $nextToken: String
  ) {
    getDestinationsByConversation(
      conversationId: $conversationId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        ...DestinationFields
      }
      nextToken
      total
    }
  }
  ${DESTINATION_FRAGMENT}
`;

// Mutations
export const CREATE_DESTINATION = gql`
  mutation CreateDestination($input: CreateDestinationInput!) {
    createDestination(input: $input) {
      ...DestinationFields
    }
  }
  ${DESTINATION_FRAGMENT}
`;

export const UPDATE_DESTINATION = gql`
  mutation UpdateDestination($input: UpdateDestinationInput!) {
    updateDestination(input: $input) {
      ...DestinationFields
    }
  }
  ${DESTINATION_FRAGMENT}
`;

export const DELETE_DESTINATION = gql`
  mutation DeleteDestination($id: ID!) {
    deleteDestination(id: $id)
  }
`;

export const ATTACH_DESTINATION_TO_CONVERSATION = gql`
  mutation AttachDestinationToConversation(
    $destinationId: ID!
    $conversationId: ID!
  ) {
    attachDestinationToConversation(
      destinationId: $destinationId
      conversationId: $conversationId
    )
  }
`;

export const DETACH_DESTINATION_FROM_CONVERSATION = gql`
  mutation DetachDestinationFromConversation(
    $destinationId: ID!
    $conversationId: ID!
  ) {
    detachDestinationFromConversation(
      destinationId: $destinationId
      conversationId: $conversationId
    )
  }
`;

// Subscriptions
export const ON_DESTINATION_UPDATED = gql`
  subscription OnDestinationUpdated($tenantId: ID!) {
    onDestinationUpdated(tenantId: $tenantId) {
      ...DestinationFields
    }
  }
  ${DESTINATION_FRAGMENT}
`;

export const ON_DESTINATION_ANALYTICS_UPDATED = gql`
  subscription OnDestinationAnalyticsUpdated($destinationId: ID!) {
    onDestinationAnalyticsUpdated(destinationId: $destinationId) {
      ...DestinationAnalyticsFields
    }
  }
  ${DESTINATION_ANALYTICS_FRAGMENT}
`;

// Analytics queries
export const GET_TENANT_DESTINATION_ANALYTICS = gql`
  query GetTenantDestinationAnalytics($dateFrom: String, $dateTo: String) {
    getTenantDestinationAnalytics(dateFrom: $dateFrom, dateTo: $dateTo) {
      tenantId
      totalDestinations
      totalClicks
      uniqueClicks
      averageConversionRate
      deviceBreakdown {
        PHONE
        DESKTOP
        TABLET
        OTHER
      }
      topDestinations {
        destinationId
        name
        clicks
        conversionRate
      }
    }
  }
`;
